import React, { useEffect, useRef, useState } from "react";
import { api, imageUrl } from "@/lib/api";
import { useDepartments } from "@/contexts/DepartmentsContext";
import { useStockTypes } from "@/contexts/StockTypesContext";
import { useMaterials } from "@/contexts/MaterialsContext";
import { useColours } from "@/contexts/ColoursContext";
import { toast } from "sonner";
import StockThumb from "@/components/StockThumb";
import MultiImageUpload from "@/components/MultiImageUpload";
import Lightbox from "@/components/Lightbox";
import TemplateYieldsEditor from "@/components/TemplateYieldsEditor";
import CopyYieldsDialog from "@/components/CopyYieldsDialog";
import { X, Save, Trash2, DollarSign, MapPin, Copy, ShieldAlert, Upload } from "lucide-react";
import QuickAddSelect from "@/components/QuickAddSelect";
import SubstitutesCard from "@/components/SubstitutesCard";

const DEPARTMENTS_FALLBACK = ["Production", "Milling", "3DP", "Reception"];

// Grammar helper shared across all pluralisation on the cost card.
// Skips 's' for uncountable measurement units, keeps singular at n=1,
// avoids double-'s' on words already ending in "s".
const UNCOUNTABLE_UNITS = new Set([
  "kg", "g", "L", "l", "ml", "mL", "m", "cm", "mm", "oz", "lb",
]);
const pluralUnit = (n, u) => {
  if (!u) return "";
  if (Math.abs(Number(n)) === 1) return u;
  if (UNCOUNTABLE_UNITS.has(u)) return u;
  return u.endsWith("s") ? u : `${u}s`;
};

const MSDSUpload = ({ value, onChange }) => {
  const [busy, setBusy] = useState(false);
  const onPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.path);
      toast.success("MSDS uploaded");
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  return (
    <label>
      <div className="label-eyebrow mb-1">MSDS document</div>
      <div className="flex items-center gap-2">
        {value ? (
          <a
            data-testid="stock-msds-link"
            href={imageUrl(value)}
            target="_blank"
            rel="noreferrer"
            className="nb-input flex items-center gap-2 truncate text-[#228EBF] underline-offset-2 hover:underline"
          >
            View document
          </a>
        ) : (
          <div className="nb-input flex items-center text-[#607087] italic text-sm">
            No file yet
          </div>
        )}
        <label className="nb-btn bg-white py-2 px-3 flex items-center gap-1.5 text-xs cursor-pointer whitespace-nowrap">
          <Upload size={13} strokeWidth={2.5} />
          {busy ? "..." : value ? "Replace" : "Upload"}
          <input
            data-testid="stock-msds-upload-input"
            type="file"
            accept=".pdf,.png,.jpg,.jpeg,.heic"
            className="hidden"
            onChange={onPick}
          />
        </label>
        {value && (
          <button
            type="button"
            data-testid="stock-msds-clear"
            onClick={() => onChange("")}
            className="nb-btn bg-white p-2"
            aria-label="clear msds"
          >
            <X size={14} strokeWidth={2.5} />
          </button>
        )}
      </div>
    </label>
  );
};

const empty = {
  name: "",
  type: "sheet",
  unit: "sheet",
  sku: "",
  category: "Material",
  supplier_id: "",
  supplier_default: "",
  order_contact_id: "",
  size_spec: "",
  current_qty: 0,
  low_stock_threshold: 0,
  image_paths: [],
  color_tag: "#001740",
  notes: "",
  unit_cost: 0,
  reorder_weeks: 4,
  template_yields: [],
  bin_location: "",
  roll_length_m: "",
  pack_size: "",
  pack_unit: "",
  material: "",
  colour: "",
  length_mm: "",
  width_mm: "",
  thickness_mm: "",
  department: "",
  departments: [],
  is_hazardous: false,
  msds_url: "",
  expiry_date: "",
  expiry_remind_days: 30,
  // Auto-Processor per item — pre-fills the amber Toll Processing
  // block on the Orders page whenever this stock is on a new draft PO.
  default_processor_id: "",
  default_processor_fee_per_log: "",
};

const StockFormDialog = ({ open, initial, onClose, onSaved, onDelete }) => {
  const { names: deptNames, allowedTypesFor } = useDepartments();
  const { types: stockTypes, isDimensional } = useStockTypes();
  const { names: materialNames, coloursFor, thumbnailOf, thicknessesFor, reload: reloadMaterials } = useMaterials();
  const { colours: colourList, reload: reloadColours } = useColours();
  const DEPARTMENTS = deptNames?.length ? deptNames : DEPARTMENTS_FALLBACK;
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  // Suppliers flagged role=processor|both — populates the Auto-Processor
  // dropdown lower down the form. Fetched once per open so newly-added
  // processors on the Suppliers page appear next time the dialog opens.
  const [processors, setProcessors] = useState([]);
  useEffect(() => {
    if (!open) return;
    api
      .get("/suppliers")
      .then(({ data }) =>
        setProcessors(
          (data || []).filter(
            (s) =>
              s.is_active !== false &&
              (s.role === "processor" || s.role === "both"),
          ),
        ),
      )
      .catch(() => setProcessors([]));
  }, [open]);
  const [previewIdx, setPreviewIdx] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [stockCategories, setStockCategories] = useState([]);
  const [machinesAll, setMachinesAll] = useState([]);
  const [linkedMachineIds, setLinkedMachineIds] = useState([]);
  const [machineFilter, setMachineFilter] = useState("");
  // Team members with a stored email — the pool the "Order contact"
  // dropdown draws from. Loaded once when the dialog opens so the
  // manager can pick whoever normally handles the reorder for this line.
  const [teamWithEmail, setTeamWithEmail] = useState([]);

  const loadSuppliers = () =>
    api.get("/suppliers").then((r) => setSuppliers(r.data)).catch(() => {});
  const loadStockCategories = () =>
    api
      .get("/stock-categories")
      .then((r) => setStockCategories(r.data))
      .catch(() => {});
  const [copyOpen, setCopyOpen] = useState(false);
  // When true, the currently-open dialog started as an edit but the user
  // clicked "Duplicate" — Save should POST a new item instead of PUT-ing.
  const [duplicating, setDuplicating] = useState(false);

  const nameInputRef = useRef(null);

  const duplicate = () => {
    if (!initial?.id) return;
    setForm((f) => ({
      ...f,
      // Critical: strip identity so save() POSTs a fresh row instead of
      // aliasing the original's id (see iter_18 report).
      id: undefined,
      _id: undefined,
      name: f.name ? `${f.name.trim()} (copy)` : "",
      current_qty: 0,
      sku: "",
      notes: "",
      batch_ref: "",
      color_tag: "",
      // Keep image_paths — cloning is usually the *same* material shot
      // (different size/spec), so re-uploading the same photos is friction
      // users flagged. Remove per-item in the form if they don't apply.
      created_at: undefined,
      updated_at: undefined,
      expiry_date: "",
      // Cost fields deliberately left in — same material, likely same price.
    }));
    setDuplicating(true);
    setTimeout(() => nameInputRef.current?.focus(), 60);
    toast.info("Duplicating — edit name, qty and photos, then Save");
  };
  // Cost grid: tracks which field is being typed in so we don't reformat
  // the user's keystrokes (was causing "4" to snap to "4.00" mid-typing).
  const [costFocus, setCostFocus] = useState(null);
  const [costDraft, setCostDraft] = useState("");

  useEffect(() => {
    if (open) {
      loadSuppliers();
      loadStockCategories();
      // Load every machine once so the linked-machines picker can render.
      // Cheap: ~10-30 rows in practice.
      api
        .get("/machines")
        .then(({ data }) => setMachinesAll(data || []))
        .catch(() => setMachinesAll([]));
      // Team members with an email — feeds the "Order contact" dropdown.
      api
        .get("/users/public")
        .then(({ data }) => {
          const list = (data || []).filter(
            (u) => u.is_active !== false && (u.email || "").trim(),
          );
          setTeamWithEmail(list);
        })
        .catch(() => setTeamWithEmail([]));
    }
  }, [open]);

  // On edit: preload which machines already list this stock as a spare so
  // the checkbox column starts in the right state. New items start empty.
  useEffect(() => {
    if (!open) return;
    if (initial?.id) {
      api
        .get(`/stock/${initial.id}/linked-machines`)
        .then(({ data }) => setLinkedMachineIds((data || []).map((m) => m.id)))
        .catch(() => setLinkedMachineIds([]));
    } else {
      setLinkedMachineIds([]);
    }
    setMachineFilter("");
  }, [open, initial?.id]);

  useEffect(() => {
    if (open) {
      if (initial) {
        const paths = initial.image_paths?.length
          ? initial.image_paths
          : initial.image_path
          ? [initial.image_path]
          : [];
        // Round any DB-side IEEE-754 tails on numeric fields (e.g.
        // 6.550000000000001 from Math on the take endpoint) so the
        // Current qty / threshold inputs open with a clean value the
        // user can eyeball and edit. Preserve integers as-is.
        const cleanNum = (n) => {
          if (n === null || n === undefined || n === "") return n;
          const v = Number(n);
          if (!Number.isFinite(v)) return n;
          return Number.isInteger(v) ? v : Number(v.toFixed(2));
        };
        setForm({
          ...empty,
          ...initial,
          current_qty: cleanNum(initial.current_qty),
          low_stock_threshold: cleanNum(initial.low_stock_threshold),
          unit_cost: cleanNum(initial.unit_cost),
          departments: Array.isArray(initial.departments)
            ? initial.departments
            : initial.department
              ? [initial.department]
              : [],
          image_paths: paths,
        });
      } else {
        setForm(empty);
      }
      setDuplicating(false);
    }
  }, [open, initial]);

  if (!open) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onTypeChange = (t) => {
    const def = stockTypes.find((x) => x.name === t);
    setForm((f) => ({ ...f, type: t, unit: f.unit || def?.default_unit || "unit" }));
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    setBusy(true);
    try {
      // Unit is auto-derived from Type since the visible field was removed.
      // For new items with an empty unit, back-fill from the type's default.
      const typeDef = stockTypes.find((t) => t.name === form.type);
      const resolvedUnit = form.unit || typeDef?.default_unit || "unit";
      const payload = {
        ...form,
        unit: resolvedUnit,
        current_qty: Number(form.current_qty) || 0,
        low_stock_threshold: Number(form.low_stock_threshold) || 0,
        unit_cost: Number(form.unit_cost) || 0,
        reorder_weeks: Number(form.reorder_weeks) || 4,
        roll_length_m:
          form.type === "roll" && form.roll_length_m !== "" && form.roll_length_m !== null
            ? Number(form.roll_length_m) || null
            : null,
        pack_size:
          form.type !== "roll" && form.pack_size !== "" && form.pack_size !== null
            ? Number(form.pack_size) || null
            : null,
        pack_unit:
          form.type !== "roll" && form.pack_unit
            ? String(form.pack_unit).trim() || null
            : null,
        length_mm:
          isDimensional(form.type) && form.length_mm !== "" && form.length_mm !== null
            ? Number(form.length_mm) || null
            : null,
        width_mm:
          isDimensional(form.type) && form.width_mm !== "" && form.width_mm !== null
            ? Number(form.width_mm) || null
            : null,
        thickness_mm:
          isDimensional(form.type) && form.thickness_mm !== "" && form.thickness_mm !== null
            ? Number(form.thickness_mm) || null
            : null,
        department: form.departments?.[0] || form.department || null,
        departments: Array.isArray(form.departments) ? form.departments : [],
        material: form.material || null,
        colour: form.colour || null,
        is_hazardous: !!form.is_hazardous,
        msds_url: (form.msds_url || "").trim() || null,
        expiry_date: form.expiry_date || null,
        expiry_remind_days: Number(form.expiry_remind_days) || 30,
        supplier_id: form.supplier_id || null,
        order_contact_id: form.order_contact_id || null,
        default_processor_id: form.default_processor_id || null,
        default_processor_fee_per_log:
          form.default_processor_fee_per_log === "" ||
          form.default_processor_fee_per_log == null
            ? null
            : Number(form.default_processor_fee_per_log) || null,
        template_yields: (form.template_yields || [])
          .filter((y) => y.name && Number(y.qty_per_unit) > 0)
          .map((y) => ({ name: y.name, qty_per_unit: Number(y.qty_per_unit) })),
        image_path: form.image_paths?.[0] || null, // back-compat field
      };
      if (duplicating) {
        delete payload.substitute_ids;
        delete payload.discontinued;
        delete payload.replaced_by_id;
      }
      if (initial?.id && !duplicating) {
        const { data } = await api.put(`/stock/${initial.id}`, payload);
        // Sync linked machines (idempotent). Manager-only endpoint — will
        // 403 silently for staff and we surface a friendly toast.
        try {
          await api.put(`/stock/${initial.id}/linked-machines`, {
            machine_ids: linkedMachineIds,
          });
        } catch (linkErr) {
          if (linkErr.response?.status !== 403) {
            toast.error("Item saved, but linked-machines sync failed");
          }
        }
        toast.success("Updated");
        onSaved?.(data);
      } else {
        const { data } = await api.post("/stock", payload);
        if (linkedMachineIds.length > 0 && data?.id) {
          try {
            await api.put(`/stock/${data.id}/linked-machines`, {
              machine_ids: linkedMachineIds,
            });
          } catch (linkErr) {
            if (linkErr.response?.status !== 403) {
              toast.error("Item created, but linked-machines sync failed");
            }
          }
        }
        toast.success(duplicating ? `Cloned as "${data.name}"` : "Added to catalog");
        onSaved?.(data);
      }
      setDuplicating(false);
      // Broadcast so Layout's alert bell (and any other listener) can refresh
      // its low-stock / expiry counts immediately.
      window.dispatchEvent(new CustomEvent("stock-updated"));
      onClose();
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setBusy(false);
    }
  };

  const del = async () => {
    if (!initial?.id) return;
    if (!window.confirm(`Delete "${form.name}" from catalog?`)) return;
    setBusy(true);
    try {
      await api.delete(`/stock/${initial.id}`);
      toast.success("Deleted");
      onDelete?.(initial.id);
      window.dispatchEvent(new CustomEvent("stock-updated"));
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 overflow-y-auto"
      data-testid="stock-form-overlay"
      onClick={onClose}
    >
      <div className="min-h-full flex items-start sm:items-center justify-center p-4">
        <div
          className="nb-card p-6 w-full max-w-4xl my-6"
          onClick={(e) => e.stopPropagation()}
          data-testid="stock-form-dialog"
        >
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="label-eyebrow">
              {initial?.id && !duplicating
                ? "Edit item"
                : duplicating
                ? "Duplicating"
                : "New inventory item"}
            </div>
            <h3 className="font-display text-3xl mt-1">
              {initial?.id && !duplicating
                ? form.name
                : duplicating
                ? form.name || "Cloned item"
                : "Add item"}
            </h3>
          </div>
          <button
            data-testid="stock-form-close"
            onClick={onClose}
            className="nb-btn bg-white p-2"
          >
            <X size={18} strokeWidth={2.5} />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {/* Image panel — narrower now so the field grid breathes. */}
          <div className="md:col-span-1 flex flex-col gap-3">
            <StockThumb item={form} size="h-32 w-32 mx-auto" />

            <MultiImageUpload
              paths={form.image_paths}
              onChange={(arr) => set("image_paths", arr)}
              onPreview={(i) => setPreviewIdx(i)}
            />
          </div>

          {/* Fields */}
          <div className="md:col-span-3 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
            <label className="sm:col-span-2">
              <div className="label-eyebrow mb-1">Name *</div>
              <input
                ref={nameInputRef}
                data-testid="stock-name-input"
                className="nb-input"
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                placeholder="e.g. EVA Foam 6mm Black"
              />
            </label>

            <label>
              <div className="label-eyebrow mb-1">Type *</div>
              {(() => {
                const allowed = (form.departments || []).length
                  ? allowedTypesFor(form.departments[0])
                  : null;
                const visible = allowed
                  ? stockTypes.filter((t) => allowed.includes(t.name))
                  : stockTypes;
                const list = visible.length ? visible : stockTypes; // fallback safety
                return (
                  <select
                    data-testid="stock-type-select"
                    className="nb-input"
                    value={form.type}
                    onChange={(e) => onTypeChange(e.target.value)}
                  >
                    {list.map((t) => (
                      <option key={t.name} value={t.name}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                );
              })()}
            </label>

            {isDimensional(form.type) && (
              <div className="sm:col-span-2 nb-card-sm p-3 bg-[#F4F8FB]">
                <div className="label-eyebrow mb-2 text-[#228EBF]">
                  Dimensions (mm) — optional
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  <label>
                    <div className="label-eyebrow mb-1">Length</div>
                    <input
                      data-testid="stock-length-mm-input"
                      type="number"
                      step="0.1"
                      className="nb-input font-mono-num"
                      value={form.length_mm ?? ""}
                      onChange={(e) => set("length_mm", e.target.value)}
                      placeholder="mm"
                    />
                  </label>
                  <label>
                    <div className="label-eyebrow mb-1">Width</div>
                    <input
                      data-testid="stock-width-mm-input"
                      type="number"
                      step="0.1"
                      className="nb-input font-mono-num"
                      value={form.width_mm ?? ""}
                      onChange={(e) => set("width_mm", e.target.value)}
                      placeholder="mm"
                    />
                  </label>
                  <label>
                    <div className="label-eyebrow mb-1">Thickness</div>
                    {(() => {
                      const preset = thicknessesFor(form.material);
                      if (preset && preset.length > 0) {
                        return (
                          <select
                            data-testid="stock-thickness-mm-select"
                            className="nb-input font-mono-num"
                            value={form.thickness_mm ?? ""}
                            onChange={(e) =>
                              set(
                                "thickness_mm",
                                e.target.value === "" ? "" : Number(e.target.value),
                              )
                            }
                          >
                            <option value="">— pick —</option>
                            {preset.map((t) => (
                              <option key={t} value={t}>
                                {t} mm
                              </option>
                            ))}
                          </select>
                        );
                      }
                      return (
                        <input
                          data-testid="stock-thickness-mm-input"
                          type="number"
                          step="0.1"
                          className="nb-input font-mono-num"
                          value={form.thickness_mm ?? ""}
                          onChange={(e) => set("thickness_mm", e.target.value)}
                          placeholder="mm"
                        />
                      );
                    })()}
                  </label>
                </div>
              </div>
            )}

            <label>
              <div className="label-eyebrow mb-1">SKU / barcode</div>
              <input
                data-testid="stock-sku-input"
                className="nb-input"
                value={form.sku || ""}
                onChange={(e) => set("sku", e.target.value)}
                placeholder="scanned or typed"
              />
            </label>

            <label>
              <div className="label-eyebrow mb-1">Size / spec label</div>
              <input
                data-testid="stock-size-spec-input"
                className="nb-input"
                value={form.size_spec || ""}
                onChange={(e) => set("size_spec", e.target.value)}
                placeholder='e.g. "Size 3", "13cm x 18m", "Large"'
              />
              <div className="text-[11px] text-[#607087] mt-1">
                Free-text label shown as a chip on the Inventory card next to the type.
              </div>
            </label>

            <label>
              <div className="label-eyebrow mb-1">Supplier</div>
              <QuickAddSelect
                testidPrefix="stock-supplier"
                value={form.supplier_id || ""}
                onChange={(v) => set("supplier_id", v)}
                options={suppliers}
                valueKey="id"
                labelKey="name"
                endpoint="/suppliers"
                placeholder="— None —"
                onCreated={() => loadSuppliers()}
              />
            </label>

            <label>
              <div className="label-eyebrow mb-1">Order contact</div>
              <select
                data-testid="stock-order-contact-select"
                className="nb-input"
                value={form.order_contact_id || ""}
                onChange={(e) => set("order_contact_id", e.target.value)}
              >
                <option value="">— None —</option>
                {teamWithEmail.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name} · {u.email}
                  </option>
                ))}
              </select>
              <div className="text-[11px] text-[#607087] mt-1">
                Auto-picked as the primary To on Send PO → Ops. Only team
                members with an email on file appear here.
              </div>
            </label>

            <label>
              <div className="label-eyebrow mb-1">Category</div>
              <QuickAddSelect
                testidPrefix="stock-category"
                value={form.category || "Material"}
                onChange={(v) => set("category", v)}
                options={stockCategories}
                endpoint="/stock-categories"
                placeholder="— None —"
                onCreated={() => loadStockCategories()}
              />
            </label>

            <label>
              <div className="label-eyebrow mb-1">Departments</div>
              <div
                data-testid="stock-departments-chips"
                className="flex flex-wrap gap-1.5 border-2 border-black rounded-xl p-2 bg-white min-h-[44px]"
              >
                {DEPARTMENTS.map((d) => {
                  const list = form.departments || [];
                  const active = list.includes(d);
                  const isPrimary = active && list[0] === d;
                  return (
                    <span
                      key={d}
                      className={`inline-flex items-stretch rounded-md overflow-hidden border-2 border-black transition-all ${
                        active ? "bg-[#001740] text-white" : "bg-[#F4F8FB]"
                      }`}
                    >
                      <button
                        type="button"
                        data-testid={`stock-dept-chip-${d}`}
                        onClick={() =>
                          setForm((f) => {
                            const cur = f.departments || [];
                            const next = cur.includes(d)
                              ? cur.filter((x) => x !== d)
                              : [...cur, d];
                            return { ...f, departments: next };
                          })
                        }
                        className="px-2.5 py-1 text-xs font-bold flex items-center gap-1"
                      >
                        {isPrimary && <span aria-hidden>★</span>}
                        {d}
                      </button>
                      {active && !isPrimary && (
                        <button
                          type="button"
                          data-testid={`stock-dept-primary-${d}`}
                          title={`Make ${d} the primary owner (used by Reports)`}
                          onClick={() =>
                            setForm((f) => {
                              const cur = f.departments || [];
                              const others = cur.filter((x) => x !== d);
                              return { ...f, departments: [d, ...others] };
                            })
                          }
                          className="px-1.5 border-l-2 border-black bg-white/10 hover:bg-white/25 text-xs"
                        >
                          ☆
                        </button>
                      )}
                    </span>
                  );
                })}
                {(form.departments || []).length === 0 && (
                  <span className="text-xs text-[#607087] italic self-center px-2">
                    — Unassigned —
                  </span>
                )}
              </div>
              <div className="text-[10px] text-[#607087] mt-1">
                Pick 1 or more. Staff pick which one at Take Stock. <b>★</b>{" "}
                marks the <b>primary owner</b> — used by Reports Stock-value
                card and the "Primary owner" drill-down tab. Tap the ☆ on any
                active chip to make it primary.
              </div>
            </label>

            <label>
              <div className="label-eyebrow mb-1">Material family</div>
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <QuickAddSelect
                    testidPrefix="stock-material"
                    value={form.material || ""}
                    onChange={(v) => set("material", v)}
                    options={materialNames}
                    endpoint="/materials"
                    placeholder="— Unassigned —"
                    onCreated={() => reloadMaterials?.()}
                  />
                </div>
                {form.material && thumbnailOf(form.material) && (
                  <img
                    data-testid="stock-material-thumb"
                    src={imageUrl(thumbnailOf(form.material))}
                    alt={form.material}
                    className="w-7 h-7 rounded-md border-2 border-black object-cover"
                  />
                )}
              </div>
              {materialNames.length === 0 && (
                <div className="label-eyebrow mt-1 text-[#607087]">
                  Add materials in Inventory → Manage materials
                </div>
              )}
            </label>

            <label>
              <div className="label-eyebrow mb-1">Colour</div>
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  {(() => {
                    const palette = coloursFor(form.material);
                    const allowed = palette
                      ? colourList.filter((c) => palette.includes(c.name))
                      : colourList;
                    // Always include current value if it's not in the palette
                    // anymore (avoid silently changing legacy items).
                    const visible = [...allowed];
                    if (
                      form.colour &&
                      !visible.some((c) => c.name === form.colour)
                    ) {
                      const orphan = colourList.find(
                        (c) => c.name === form.colour,
                      );
                      visible.push(
                        orphan || {
                          id: `legacy-${form.colour}`,
                          name: form.colour,
                          swatch: null,
                        },
                      );
                    }
                    return (
                      <QuickAddSelect
                        testidPrefix="stock-colour"
                        value={form.colour || ""}
                        onChange={(v) => set("colour", v)}
                        options={visible}
                        endpoint="/colours"
                        placeholder="— Unassigned —"
                        onCreated={() => reloadColours?.()}
                      />
                    );
                  })()}
                </div>
                {form.colour && (() => {
                  const c = colourList.find((c) => c.name === form.colour);
                  if (c?.swatch_image) {
                    return (
                      <img
                        data-testid="stock-colour-image"
                        src={imageUrl(c.swatch_image)}
                        alt={form.colour}
                        className="w-7 h-7 rounded-md border-2 border-black object-cover"
                      />
                    );
                  }
                  if (c?.swatch) {
                    return (
                      <span
                        data-testid="stock-colour-swatch"
                        className="inline-block w-7 h-7 rounded-md border-2 border-black"
                        style={{ background: c.swatch }}
                        aria-label={`swatch ${form.colour}`}
                      />
                    );
                  }
                  return null;
                })()}
              </div>
              {colourList.length === 0 && (
                <div className="label-eyebrow mt-1 text-[#607087]">
                  Add colours in Inventory → Manage colours
                </div>
              )}
              {form.material && coloursFor(form.material) && (
                <div
                  data-testid="stock-colour-palette-hint"
                  className="label-eyebrow mt-1 text-[#607087]"
                >
                  Showing colours available for {form.material}
                </div>
              )}
            </label>

            <label>
              <div className="label-eyebrow mb-1">Current qty</div>
              <input
                data-testid="stock-qty-input"
                className="nb-input font-mono-num"
                type="number"
                step="0.01"
                value={form.current_qty}
                onChange={(e) => set("current_qty", e.target.value)}
              />
            </label>

            <label>
              <div className="label-eyebrow mb-1">Low-stock alert at</div>
              <input
                data-testid="stock-threshold-input"
                className="nb-input font-mono-num"
                type="number"
                step="0.01"
                value={form.low_stock_threshold}
                onChange={(e) => set("low_stock_threshold", e.target.value)}
              />
            </label>

            <div className="sm:col-span-2">
              <details
                className="border-2 border-black rounded-lg bg-[#FFF8E1]"
                open={!!form.default_processor_id}
                data-testid="stock-toll-processing-block"
              >
                <summary className="cursor-pointer px-3 py-2 text-xs font-bold uppercase tracking-wider flex items-center justify-between gap-2">
                  <span>Toll processing (auto-attach on new POs)</span>
                  {form.default_processor_id && (
                    <span className="text-[10px] font-bold bg-[#E5A72E] text-black px-1.5 py-0.5 rounded normal-case tracking-normal">
                      {processors.find((p) => p.id === form.default_processor_id)?.name || "set"}
                    </span>
                  )}
                </summary>
                <div className="px-3 pb-3 pt-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <label>
                    <div className="label-eyebrow mb-1">Default processor</div>
                    <select
                      data-testid="stock-default-processor"
                      className="nb-input py-2 text-sm"
                      value={form.default_processor_id || ""}
                      onChange={(e) => set("default_processor_id", e.target.value)}
                    >
                      <option value="">— none —</option>
                      {processors.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                    {processors.length === 0 && (
                      <div className="text-[11px] text-[#8A1F1F] mt-1">
                        No processors yet — flag one on the Suppliers page.
                      </div>
                    )}
                  </label>
                  <label>
                    <div className="label-eyebrow mb-1">Fee per log ($)</div>
                    <input
                      data-testid="stock-default-processor-fee"
                      type="number"
                      step="0.01"
                      min="0"
                      className="nb-input py-2 text-sm font-mono-num"
                      value={form.default_processor_fee_per_log || ""}
                      onChange={(e) => set("default_processor_fee_per_log", e.target.value)}
                      placeholder="e.g. 25.00"
                    />
                  </label>
                </div>
                <div className="px-3 pb-3 text-[11px] text-[#7A5300]">
                  When this item is on a new draft PO, the amber Toll
                  Processing block on Orders auto-fills with these values
                  · 10 wheels per log.
                </div>
              </details>
            </div>

            <label className="sm:col-span-2">
              <div className="label-eyebrow mb-1">Notes</div>
              <textarea
                data-testid="stock-notes-input"
                className="nb-input"
                rows={2}
                value={form.notes || ""}
                onChange={(e) => set("notes", e.target.value)}
              />
            </label>
          </div>
        </div>

        {/* Cost, location & template yields — cleanly stacked */}
        <div className="mt-6 flex flex-col gap-4">
          {/* Card 1: Cost & Reorder */}
          <div className="nb-card-sm p-4">
            <div className="flex items-center gap-2 mb-3">
              <DollarSign size={16} strokeWidth={2.5} className="text-[#228EBF]" />
              <h4 className="font-display text-sm tracking-wide">Cost & Reorder</h4>
            </div>
            {(() => {
              const qty = Number(form.current_qty) || 0;
              const cost = Number(form.unit_cost) || 0;
              const size = Number(form.pack_size) || 0;
              const unitLbl = form.unit || "unit";
              const hasPack = size > 0 && !!form.pack_unit;
              const total = cost * qty;
              const perSub = hasPack && size > 0 ? cost / size : 0;
              const plural = pluralUnit;
              const setFromTotal = (v) => {
                const t = Number(v);
                if (qty > 0 && !Number.isNaN(t)) {
                  set("unit_cost", (t / qty).toFixed(4));
                } else if (v === "") {
                  set("unit_cost", "");
                }
              };
              const setFromPerPack = (v) => set("unit_cost", v);
              const setFromPerSub = (v) => {
                const s = Number(v);
                if (size > 0 && !Number.isNaN(s)) {
                  set("unit_cost", (s * size).toFixed(4));
                } else if (v === "") {
                  set("unit_cost", "");
                }
              };
              // Displayed value comes from the user's keystrokes while they're
              // focused on a field, otherwise it's the recomputed value. This
              // prevents toFixed() from re-formatting mid-typing and yanking
              // the caret to the end.
              const displayTotal =
                costFocus === "total"
                  ? costDraft
                  : total > 0
                  ? total.toFixed(2)
                  : "";
              const displayPerPack =
                costFocus === "perPack" ? costDraft : form.unit_cost ?? "";
              const displayPerSub =
                costFocus === "perSub"
                  ? costDraft
                  : perSub > 0
                  ? perSub.toFixed(4)
                  : "";
              const onFocus = (which, initial) => () => {
                setCostFocus(which);
                setCostDraft(String(initial ?? ""));
              };
              const onBlur = () => {
                setCostFocus(null);
                setCostDraft("");
              };
              const onChange = (which, setter) => (e) => {
                setCostDraft(e.target.value);
                if (costFocus !== which) setCostFocus(which);
                setter(e.target.value);
              };
              return (
                <>
                  {/* Reading check — a plain-English one-liner describing the
                      whole item as currently configured. Surfaces set-up
                      mistakes at a glance ("You have 50 sheets on hand, each
                      containing 1 bag" flags the classic pack_size=1 error). */}
                  {(qty > 0 || cost > 0) && (
                    <div
                      data-testid="reading-check"
                      className="mb-4 nb-card-sm p-3 bg-[#F1E7FA] border-[#5B3A99]"
                    >
                      <div className="label-eyebrow mb-1 text-[#5B3A99]">
                        Reading check
                      </div>
                      <div className="font-display text-sm leading-snug text-[#3D1F6E]">
                        {(() => {
                          const name = form.name?.trim() || "this item";
                          const parts = [];
                          const qtyDisp = qty.toLocaleString();
                          if (qty > 0) {
                            parts.push(
                              `You have ${qtyDisp} ${plural(qty, unitLbl)} of ${name} on hand`,
                            );
                          }
                          if (cost > 0 && qty > 0) {
                            parts.push(
                              `worth $${total.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })} (${qtyDisp} × $${cost.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}/${unitLbl})`,
                            );
                          } else if (cost > 0) {
                            parts.push(
                              `at $${cost.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })} per ${unitLbl}`,
                            );
                          }
                          if (hasPack) {
                            const subTotal = qty * size;
                            parts.push(
                              `each ${unitLbl} contains ${size} ${plural(size, form.pack_unit)}` +
                                (qty > 0
                                  ? ` (${subTotal.toLocaleString()} ${plural(subTotal, form.pack_unit)} total` +
                                    (perSub > 0
                                      ? ` at $${perSub.toFixed(perSub < 1 ? 4 : 2)}/${form.pack_unit})`
                                      : ")")
                                  : ""),
                            );
                          }
                          const sentence = parts.join(", ") + (parts.length ? "." : "");
                          return sentence || "Fill in quantity and cost to see a summary.";
                        })()}
                      </div>
                      {/* Sanity flag — surface the classic pack_size=1
                          misconfiguration since it usually means the user
                          intended base-unit vs pack-unit swapped. */}
                      {hasPack && size === 1 && (
                        <div className="label-eyebrow mt-2 text-[#8A1F1F]">
                          ⚠ Pack size = 1 usually means Base unit and Pack unit
                          are swapped. If a bag holds 50 items, set Base unit ={" "}
                          <b>bag</b>, How many = <b>50</b>, Of what ={" "}
                          <b>items</b>.
                        </div>
                      )}
                    </div>
                  )}

                  {/* Base unit: what the user counts on hand. Defaults from
                      the picked stock type, but exposed here so it's easy to
                      pick the right thing (bag, tub, roll, sheet, item…) —
                      previously buried, causing users to leave it at the
                      default "sheet" even when tracking bags. */}
                  <label className="block mb-3">
                    <div className="label-eyebrow mb-1">Base unit — what one item is called</div>
                    <input
                      data-testid="stock-base-unit-input"
                      className="nb-input font-mono-num"
                      list="base-unit-suggestions"
                      value={form.unit || ""}
                      onChange={(e) => set("unit", e.target.value)}
                      placeholder="e.g. bag, tub, sheet, roll, item"
                    />
                    <datalist id="base-unit-suggestions">
                      <option value="bag" />
                      <option value="box" />
                      <option value="tub" />
                      <option value="roll" />
                      <option value="sheet" />
                      <option value="item" />
                      <option value="pair" />
                      <option value="unit" />
                      <option value="tube" />
                      <option value="bottle" />
                    </datalist>
                    <div className="label-eyebrow mt-1 text-[#607087]">
                      Every count, cost and stock number below is per one <b>{unitLbl}</b>.
                    </div>
                  </label>

                  <div className="label-eyebrow mb-2 text-[#607087]">
                    Fill <strong>any one</strong> — the others recalculate
                  </div>
                  <div className={`grid gap-3 ${hasPack ? "sm:grid-cols-3" : "sm:grid-cols-2"}`}>
                    <label>
                      <div className="label-eyebrow mb-1">Total purchase cost</div>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 font-mono-num text-[#607087]">$</span>
                        <input
                          data-testid="stock-total-cost-input"
                          type="number"
                          step="0.01"
                          className="nb-input font-mono-num pl-7"
                          value={displayTotal}
                          onFocus={onFocus("total", total > 0 ? total.toFixed(2) : "")}
                          onBlur={onBlur}
                          onChange={onChange("total", setFromTotal)}
                          placeholder="0.00"
                          disabled={qty <= 0}
                        />
                      </div>
                      <div className="label-eyebrow mt-1 text-[#607087]">
                        for {qty || 0} {plural(qty, unitLbl)} on hand
                      </div>
                    </label>
                    <label>
                      <div className="label-eyebrow mb-1">
                        Cost per {unitLbl}
                      </div>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 font-mono-num text-[#607087]">$</span>
                        <input
                          data-testid="stock-unit-cost-input"
                          type="number"
                          step="0.01"
                          className="nb-input font-mono-num pl-7"
                          value={displayPerPack}
                          onFocus={onFocus("perPack", form.unit_cost ?? "")}
                          onBlur={onBlur}
                          onChange={onChange("perPack", setFromPerPack)}
                          placeholder="0.00"
                        />
                      </div>
                      <div className="label-eyebrow mt-1 text-[#607087]">
                        price of one {unitLbl}
                      </div>
                    </label>
                    {hasPack && (
                      <label>
                        <div className="label-eyebrow mb-1">
                          Cost per {form.pack_unit}
                        </div>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 font-mono-num text-[#607087]">$</span>
                          <input
                            data-testid="stock-per-sub-cost-input"
                            type="number"
                            step="0.0001"
                            className="nb-input font-mono-num pl-7"
                            value={displayPerSub}
                            onFocus={onFocus("perSub", perSub > 0 ? perSub.toFixed(4) : "")}
                            onBlur={onBlur}
                            onChange={onChange("perSub", setFromPerSub)}
                            placeholder="0.0000"
                          />
                        </div>
                        <div className="label-eyebrow mt-1 text-[#607087]">
                          each {unitLbl} contains {size} {plural(size, form.pack_unit)}
                        </div>
                      </label>
                    )}
                  </div>
                  {cost > 0 && (
                    <div
                      data-testid="cost-summary"
                      className="mt-3 nb-card-sm p-2 bg-[#EAF5FB] text-sm"
                    >
                      <div className="font-mono-num">
                        Stock value on hand:{" "}
                        <strong>
                          $
                          {total.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </strong>
                        {hasPack &&
                          ` · ${(qty * size).toLocaleString()} ${plural(qty * size, form.pack_unit)} total at $${perSub.toFixed(4)}/${form.pack_unit}`}
                      </div>
                    </div>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                    <label>
                      <div className="label-eyebrow mb-1">Reorder window (weeks)</div>
                      <input
                        data-testid="stock-reorder-weeks-input"
                        type="number"
                        step="0.5"
                        className="nb-input font-mono-num"
                        value={form.reorder_weeks}
                        onChange={(e) => set("reorder_weeks", e.target.value)}
                      />
                    </label>
                  </div>
                </>
              );
            })()}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
              {form.type === "roll" && (
                <label className="sm:col-span-2">
                  <div className="label-eyebrow mb-1">Roll length (metres per full roll)</div>
                  <input
                    data-testid="stock-roll-length-input"
                    type="number"
                    step="0.1"
                    className="nb-input font-mono-num"
                    value={form.roll_length_m ?? ""}
                    onChange={(e) => set("roll_length_m", e.target.value)}
                    placeholder="e.g. 50"
                  />
                  <div className="label-eyebrow mt-1 text-[#607087]">
                    Lets staff tap &ldquo;Take 1 roll&rdquo; on the Take stock screen.
                  </div>
                </label>
              )}
              {form.type !== "roll" && (
                <div className="sm:col-span-2 nb-card-sm p-3 bg-[#FFF7E0]">
                  <div className="label-eyebrow mb-1 text-[#9A6F00]">
                    Pack content — optional
                  </div>
                  <div className="text-xs text-[#5C4A0F] mb-2">
                    Only fill this if <b>each {form.unit || "unit"}</b> contains multiple
                    sub-units you want to track (e.g., a 25 kg tub of glue, or a bag
                    of 50 pads). Leave blank if you count individual pieces.
                  </div>
                  <div className="text-xs text-[#5C4A0F] font-display mb-2">
                    Each 1 <b>{form.unit || "unit"}</b> contains…
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <label>
                      <div className="label-eyebrow mb-1">How many?</div>
                      <input
                        data-testid="stock-pack-size-input"
                        type="number"
                        step="0.1"
                        className="nb-input font-mono-num"
                        value={form.pack_size ?? ""}
                        onChange={(e) => set("pack_size", e.target.value)}
                        placeholder="e.g. 25"
                      />
                    </label>
                    <label>
                      <div className="label-eyebrow mb-1">Of what?</div>
                      <input
                        data-testid="stock-pack-unit-input"
                        className="nb-input"
                        list="pack-unit-suggestions"
                        value={form.pack_unit ?? ""}
                        onChange={(e) => set("pack_unit", e.target.value)}
                        placeholder="kg / L / pcs / items"
                      />
                      <datalist id="pack-unit-suggestions">
                        <option value="kg" />
                        <option value="g" />
                        <option value="L" />
                        <option value="ml" />
                        <option value="m" />
                        <option value="cm" />
                        <option value="pcs" />
                        <option value="items" />
                        <option value="pairs" />
                        <option value="sheets" />
                      </datalist>
                    </label>
                  </div>
                  {Number(form.pack_size) > 0 && form.pack_unit && (() => {
                    const packSize = Number(form.pack_size);
                    const liveTotal = Number(form.current_qty || 0) * packSize;
                    return (
                      <div className="label-eyebrow mt-2 text-[#9A6F00]">
                        Reads as: 1 {form.unit || "unit"} = {packSize}{" "}
                        {pluralUnit(packSize, form.pack_unit)}. Live total:{" "}
                        {liveTotal.toLocaleString()} {pluralUnit(liveTotal, form.pack_unit)}{" "}
                        on hand · cost ≈ $
                        {Number(form.unit_cost || 0) > 0 && packSize > 0
                          ? (Number(form.unit_cost) / packSize).toFixed(2)
                          : "0.00"}
                        /{form.pack_unit}.
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          </div>

          {/* Card 2: Location */}
          <div className="nb-card-sm p-4">
            <div className="flex items-center gap-2 mb-3">
              <MapPin size={16} strokeWidth={2.5} className="text-[#228EBF]" />
              <h4 className="font-display text-sm tracking-wide">Location</h4>
            </div>
            <label>
              <div className="label-eyebrow mb-1">Bin / shelf location</div>
              <input
                data-testid="stock-bin-input"
                className="nb-input"
                value={form.bin_location || ""}
                onChange={(e) => set("bin_location", e.target.value)}
                placeholder="A1-03 · Rack 4 · Mezzanine bin 7"
              />
            </label>
          </div>

          {/* Card 2b: Safety & expiry */}
          <div className="nb-card-sm p-4">
            <div className="flex items-center gap-2 mb-3">
              <ShieldAlert size={16} strokeWidth={2.5} className="text-[#E5263F]" />
              <h4 className="font-display text-sm tracking-wide">Safety & expiry</h4>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="flex items-center gap-2 nb-input cursor-pointer h-11 px-3">
                <input
                  data-testid="stock-hazardous-toggle"
                  type="checkbox"
                  className="h-5 w-5 accent-[#E5263F]"
                  checked={!!form.is_hazardous}
                  onChange={(e) => set("is_hazardous", e.target.checked)}
                />
                <span className="text-sm font-medium">Hazardous material</span>
              </label>

              <MSDSUpload
                value={form.msds_url}
                onChange={(url) => set("msds_url", url)}
              />

              <label>
                <div className="label-eyebrow mb-1">Expiry date</div>
                <input
                  data-testid="stock-expiry-date-input"
                  type="date"
                  className="nb-input font-mono-num"
                  value={form.expiry_date || ""}
                  onChange={(e) => set("expiry_date", e.target.value)}
                />
              </label>

              <label>
                <div className="label-eyebrow mb-1">Reminder (days before)</div>
                <input
                  data-testid="stock-expiry-remind-input"
                  type="number"
                  className="nb-input font-mono-num"
                  value={form.expiry_remind_days ?? 30}
                  onChange={(e) => set("expiry_remind_days", e.target.value)}
                  placeholder="30"
                />
              </label>
            </div>
          </div>

          {/* Card 3: Template Yields */}
          <div className="nb-card-sm p-4">
            <div className="flex items-center justify-between gap-3 mb-1 flex-wrap">
              <div>
                <h4 className="font-display text-sm tracking-wide">Template yields</h4>
                <div className="label-eyebrow mt-0.5">
                  Pairs of covers per 1 {form.unit} · up to 15 sizes
                </div>
              </div>
              <button
                type="button"
                data-testid="open-copy-yields"
                onClick={() => setCopyOpen(true)}
                className="nb-btn bg-white py-1.5 px-3 flex items-center gap-1.5 text-xs"
              >
                <Copy size={13} strokeWidth={2.5} /> Copy from…
              </button>
            </div>
            <TemplateYieldsEditor
              rows={form.template_yields || []}
              unit={form.unit}
              onChange={(rows) => set("template_yields", rows)}
            />
          </div>

          {/* Card 4: Linked machines */}
          <div className="nb-card-sm p-4" data-testid="stock-linked-machines-card">
            <div className="mb-2">
              <h4 className="font-display text-sm tracking-wide">
                Linked machines
              </h4>
              <div className="label-eyebrow mt-0.5">
                Tick every machine this item is used with. Taking this stock
                will prompt for the machine&apos;s hour-meter reading and
                (optionally) record it as spend on the machine.
              </div>
            </div>
            {machinesAll.length > 6 && (
              <input
                data-testid="stock-linked-machines-filter"
                className="nb-input mb-2"
                value={machineFilter}
                onChange={(e) => setMachineFilter(e.target.value)}
                placeholder="Filter machines…"
              />
            )}
            {machinesAll.length === 0 ? (
              <div className="text-xs text-[#607087] italic">
                No machines yet — add machines first from the Machinery page.
              </div>
            ) : (
              <div className="max-h-56 overflow-y-auto scrollbar-clean flex flex-col gap-1.5 pr-1">
                {machinesAll
                  .filter((m) => {
                    const q = machineFilter.trim().toLowerCase();
                    if (!q) return true;
                    return (
                      (m.name || "").toLowerCase().includes(q) ||
                      (m.category || "").toLowerCase().includes(q) ||
                      (m.department || "").toLowerCase().includes(q)
                    );
                  })
                  .map((m) => {
                    const checked = linkedMachineIds.includes(m.id);
                    return (
                      <label
                        key={m.id}
                        data-testid={`stock-link-machine-${m.id}`}
                        className={`flex items-center gap-2 rounded-md px-2 py-1.5 cursor-pointer border-2 ${
                          checked
                            ? "bg-[#F1E7FA] border-[#5B3A99]"
                            : "border-transparent hover:bg-[#F4F8FB]"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            setLinkedMachineIds((ids) =>
                              e.target.checked
                                ? [...new Set([...ids, m.id])]
                                : ids.filter((x) => x !== m.id),
                            );
                          }}
                          className="w-4 h-4"
                        />
                        <div className="flex-1 min-w-0 flex items-center gap-2 flex-wrap">
                          <span className="font-display text-sm truncate">
                            {m.name}
                          </span>
                          {m.category && (
                            <span className="label-eyebrow text-[#607087]">
                              {m.category}
                            </span>
                          )}
                          {m.department && (
                            <span className="label-eyebrow text-[#607087]">
                              · {m.department}
                            </span>
                          )}
                        </div>
                      </label>
                    );
                  })}
              </div>
            )}
            {linkedMachineIds.length > 0 && (
              <div
                data-testid="stock-linked-machines-count"
                className="label-eyebrow text-[#5B3A99] mt-2"
              >
                {linkedMachineIds.length} machine
                {linkedMachineIds.length === 1 ? "" : "s"} linked
              </div>
            )}
          </div>
          {initial?.id && !duplicating && (
            <SubstitutesCard
              itemId={initial.id}
              ids={form.substitute_ids || []}
              discontinued={form.discontinued}
              replacedById={form.replaced_by_id}
              onChange={(patch) => setForm((f) => ({ ...f, ...patch }))}
            />
          )}
        </div>

        <CopyYieldsDialog
          open={copyOpen}
          currentId={initial?.id}
          onClose={() => setCopyOpen(false)}
          onPick={(rows) => set("template_yields", rows)}
        />

        <div className="flex justify-between gap-3 mt-6 flex-wrap">
          {initial?.id && !duplicating ? (
            <button
              data-testid="stock-form-delete"
              onClick={del}
              disabled={busy}
              className="nb-btn nb-btn-danger px-4 py-3 flex items-center gap-2"
            >
              <Trash2 size={16} strokeWidth={2.5} /> Delete
            </button>
          ) : (
            <div />
          )}
          <div className="flex flex-wrap gap-3 flex-1 sm:flex-none sm:justify-end">
            {initial?.id && !duplicating && (
              <button
                data-testid="stock-form-duplicate"
                onClick={duplicate}
                className="nb-btn bg-[#F1E7FA] border-[#5B3A99] px-5 py-3 flex items-center gap-2"
                title="Clone this item — same material/type/dept, blank qty and photos"
              >
                <Copy size={16} strokeWidth={2.5} /> Duplicate
              </button>
            )}
            <button
              data-testid="stock-form-cancel"
              onClick={onClose}
              className="nb-btn bg-white px-5 py-3"
            >
              Cancel
            </button>
            <button
              data-testid="stock-form-save"
              onClick={save}
              disabled={busy}
              className="nb-btn nb-btn-primary px-6 py-3 flex items-center gap-2 flex-1 sm:flex-none justify-center"
            >
              <Save size={18} strokeWidth={2.5} /> Save
            </button>
          </div>
        </div>
        </div>
      </div>

      {previewIdx !== null && form.image_paths?.length > 0 && (
        <Lightbox
          paths={form.image_paths}
          startIndex={previewIdx}
          onClose={() => setPreviewIdx(null)}
        />
      )}
    </div>
  );
};

export default StockFormDialog;
